import {
  useTenantsTableData,
  useCreateTenant,
  useUpdateTenant,
  useDeleteTenant,
} from './useTenants';
import { TenantTableData, TenantFormData } from '../types';

// Wrapper hooks to match the DataManagement interface
export const useTenantsQuery = useTenantsTableData;

export const useCreateTenantWrapper = () => {
  const createMutation = useCreateTenant();

  return {
    mutateAsync: async (data: TenantFormData): Promise<TenantTableData> => {
      const result = await createMutation.mutateAsync({
        name: data.name,
        description: data.description,
        code: data.code,
      });

      if (result) {
        // tRPC returns ElectroDB response with tenantId, we map to id
        return {
          id: result.tenantId,
          name: result.name,
          description: result.description,
          code: result.code,
          createdAt: result.createdAt,
        };
      }

      throw new Error('Failed to create tenant');
    },
    isPending: createMutation.isPending,
  };
};

export const useUpdateTenantWrapper = () => {
  const updateMutation = useUpdateTenant();

  return {
    mutateAsync: async (params: { id: string; data: TenantFormData }): Promise<TenantTableData> => {
      const result = await updateMutation.mutateAsync({
        tenantId: params.id,
        data: {
          name: params.data.name,
          description: params.data.description,
          code: params.data.code,
        },
      });

      // Transform the result to match TenantTableData
      // tRPC returns ElectroDB response with tenantId, we map to id
      // Note: Update returns Partial type, so we need to handle undefined values
      if (result && result.tenantId && result.name && result.createdAt) {
        return {
          id: result.tenantId,
          name: result.name,
          description: result.description,
          code: result.code,
          createdAt: result.createdAt,
        };
      }

      throw new Error('Failed to update tenant');
    },
    isPending: updateMutation.isPending,
  };
};

export const useDeleteTenantWrapper = () => {
  const deleteMutation = useDeleteTenant();

  return {
    mutateAsync: async (id: string): Promise<void> => {
      // Wrap ID in object to match tRPC input schema
      await deleteMutation.mutateAsync({ id });
    },
    isPending: deleteMutation.isPending,
  };
};
